import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card, Typography, Button, Space, Input, Select, Form, Modal,
  message, Table, Tag, Popconfirm, Switch, Collapse, Divider
} from 'antd';
import {
  SettingOutlined, SaveOutlined, DeleteOutlined, EditOutlined, PlusOutlined,
  UndoOutlined
} from '@ant-design/icons';
import { useAuth } from '../utils/auth';
import aiContentService from '../services/aiContentService';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;

const PROVIDER_NAMES = {
  QWEN: '通义千问',
  DEEPSEEK: 'DeepSeek',
  OPENAI: 'OpenAI',
  CLAUDE: 'Claude',
  DOUBAO: '豆包',
  GLM: 'GLM (智谱)',
  MINIMAX: 'MiniMax',
  KIMI: 'Kimi (月之暗面)',
};

const CONTENT_TYPES = [
  { key: 'PRODUCT_PROMO', label: '带货种草' },
  { key: 'ENGAGEMENT', label: '互动福利' },
  { key: 'BRAND', label: '品牌故事' },
  { key: 'DAILY', label: '日常问候' },
  { key: 'HOLIDAY', label: '节日祝福' },
];

const AiContentConfig = ({ onSettingsChange }) => {
  const { tenant } = useAuth();
  const tenantId = tenant?.id;

  const [styleReference, setStyleReference] = useState('');
  const [defaultProvider, setDefaultProvider] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [configForm] = Form.useForm();

  // Prompt template states
  const [baseSystemPrompt, setBaseSystemPrompt] = useState('');
  const [promptOverrides, setPromptOverrides] = useState({});
  const [defaultPrompts, setDefaultPrompts] = useState({});

  // Cities config
  const [cities, setCities] = useState([]);

  const debounceRef = useRef(null);
  const promptDebounceRef = useRef(null);

  useEffect(() => {
    if (tenantId) {
      fetchSettings();
      fetchProviders();
      fetchDefaultPrompts();
    }
  }, [tenantId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await aiContentService.getTenantSettings(tenantId);
      if (response.success && response.data) {
        setStyleReference(response.data.styleReference || '');
        setDefaultProvider(response.data.defaultProvider || null);
        setBaseSystemPrompt(response.data.baseSystemPrompt || '');
        try {
          const overrides = response.data.promptOverrides ? JSON.parse(response.data.promptOverrides) : {};
          setPromptOverrides(overrides);
        } catch (e) {
          setPromptOverrides({});
        }
        try {
          const cityList = response.data.cities ? JSON.parse(response.data.cities) : [];
          setCities(cityList);
        } catch (e) {
          setCities([]);
        }
      }
    } catch (error) {
      console.error('获取配置失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await aiContentService.getProviders(tenantId);
      if (response.success) setProviders(response.data || []);
    } catch (error) {
      console.error('获取供应商失败:', error);
    }
  };

  const fetchDefaultPrompts = async () => {
    try {
      const response = await aiContentService.getDefaultPrompts();
      if (response.success) setDefaultPrompts(response.data || {});
    } catch (error) {
      console.error('获取默认Prompt失败:', error);
    }
  };

  const saveSettings = useCallback(async (updates) => {
    try {
      setSaving(true);
      const response = await aiContentService.updateTenantSettings({
        tenantId,
        ...updates,
      });
      if (response.success) {
        if (onSettingsChange) onSettingsChange(updates);
      }
    } catch (error) {
      console.error('保存配置失败:', error);
    } finally {
      setSaving(false);
    }
  }, [tenantId, onSettingsChange]);

  const handleStyleReferenceChange = (e) => {
    const value = e.target.value;
    setStyleReference(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      saveSettings({ styleReference: value, defaultProvider, cities: JSON.stringify(cities) });
    }, 1000);
  };

  const handleProviderChange = (value) => {
    setDefaultProvider(value);
    saveSettings({ styleReference, defaultProvider: value || null, cities: JSON.stringify(cities) });
  };

  const handleCitiesChange = (newCities) => {
    setCities(newCities);
    saveSettings({ styleReference, defaultProvider, cities: JSON.stringify(newCities) });
    if (onSettingsChange) onSettingsChange({ cities: newCities });
  };

  const handleSaveConfig = async () => {
    try {
      const values = await configForm.validateFields();
      const response = await aiContentService.configureProvider({
        tenantId,
        provider: values.provider,
        apiKey: values.apiKey,
        baseUrl: values.baseUrl || null,
        modelName: values.modelName || null,
        isDefault: values.isDefault || false,
      });
      if (response.success) {
        message.success('配置成功');
        setConfigModalVisible(false);
        configForm.resetFields();
        fetchProviders();
      } else {
        message.error(response.message || '配置失败');
      }
    } catch (error) {
      if (error.errorFields) return;
      message.error('配置失败');
    }
  };

  const handleToggleProvider = async (record) => {
    try {
      const response = await aiContentService.updateProvider(record.id, {
        tenantId,
        isEnabled: !record.isEnabled,
      });
      if (response.success) {
        message.success(record.isEnabled ? '已禁用' : '已启用');
        fetchProviders();
      } else {
        message.error(response.message || '操作失败');
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleDeleteProvider = async (id) => {
    try {
      const response = await aiContentService.deleteProvider(id, tenantId);
      if (response.success) {
        message.success('删除成功');
        fetchProviders();
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  // Prompt template handlers
  const savePromptSettings = useCallback((newBase, newOverrides) => {
    if (promptDebounceRef.current) clearTimeout(promptDebounceRef.current);
    promptDebounceRef.current = setTimeout(() => {
      saveSettings({
        styleReference,
        defaultProvider,
        cities: JSON.stringify(cities),
        baseSystemPrompt: newBase || null,
        promptOverrides: JSON.stringify(newOverrides),
      });
    }, 1000);
  }, [styleReference, defaultProvider, cities, saveSettings]);

  const handleBasePromptChange = (e) => {
    const value = e.target.value;
    setBaseSystemPrompt(value);
    savePromptSettings(value, promptOverrides);
  };

  const handlePromptOverrideChange = (contentType, value) => {
    const newOverrides = { ...promptOverrides, [contentType]: value || undefined };
    if (!value) delete newOverrides[contentType];
    setPromptOverrides(newOverrides);
    savePromptSettings(baseSystemPrompt, newOverrides);
  };

  const handleResetPrompt = (contentType) => {
    if (contentType === 'BASE') {
      setBaseSystemPrompt('');
      savePromptSettings('', promptOverrides);
    } else {
      const newOverrides = { ...promptOverrides };
      delete newOverrides[contentType];
      setPromptOverrides(newOverrides);
      savePromptSettings(baseSystemPrompt, newOverrides);
    }
    message.success('已恢复默认');
  };

  const providerColumns = [
    {
      title: '供应商', dataIndex: 'provider', key: 'provider', width: 150,
      render: (provider) => PROVIDER_NAMES[provider] || provider,
    },
    {
      title: '模型', dataIndex: 'modelName', key: 'modelName',
      render: (v) => v || <Text type="secondary">默认</Text>
    },
    {
      title: '默认', dataIndex: 'isDefault', key: 'isDefault', width: 80,
      render: (v) => v ? <Tag color="gold">默认</Tag> : '-'
    },
    {
      title: '状态', dataIndex: 'isEnabled', key: 'isEnabled', width: 80,
      render: (v) => v !== false ? <Tag color="green">启用</Tag> : <Tag>禁用</Tag>
    },
    {
      title: '操作', key: 'action', width: 150,
      render: (_, record) => (
        <Space size="small">
          <Switch
            size="small"
            checked={record.isEnabled !== false}
            onChange={() => handleToggleProvider(record)}
          />
          <Popconfirm title="确定删除该供应商配置？" onConfirm={() => handleDeleteProvider(record.id)} okText="确定" cancelText="取消">
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    },
  ];

  return (
    <div>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {/* 默认模型选择 */}
        <Card size="small" title="默认 AI 模型">
          <div style={{ maxWidth: 500 }}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              选择生成内容时默认使用的 AI 模型，也可以在生成时临时切换
            </Text>
            <Select
              placeholder="选择默认AI模型"
              allowClear
              style={{ width: '100%' }}
              value={defaultProvider}
              onChange={handleProviderChange}
            >
              {providers.filter(p => p.isEnabled !== false).map(p => (
                <Option key={p.provider} value={p.provider}>
                  {PROVIDER_NAMES[p.provider] || p.provider}{p.modelName ? ` - ${p.modelName}` : ''}
                </Option>
              ))}
            </Select>
          </div>
        </Card>

        {/* 供应商配置 */}
        <Card
          size="small"
          title="供应商配置"
          extra={
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setConfigModalVisible(true)}>
              添加供应商
            </Button>
          }
        >
          <Table
            columns={providerColumns}
            dataSource={providers}
            rowKey="id"
            pagination={false}
            size="small"
            locale={{ emptyText: '暂未配置任何供应商，点击上方按钮添加' }}
          />
        </Card>

        {/* 风格参考 */}
        <Card size="small" title="风格参考">
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            粘贴 3-5 条你喜欢的朋友圈文案，AI 会学习其风格来创作。修改后自动保存。
          </Text>
          <TextArea
            rows={8}
            placeholder={"粘贴3-5条你喜欢的朋友圈文案，AI会学习其风格来创作\n\n示例：\n☀️ 早安~今天的鲜榨橙汁已经备好\n0添加 · 当日现做\n戳下方小程序 享早鸟价 💛"}
            value={styleReference}
            onChange={handleStyleReferenceChange}
            maxLength={2000}
            showCount
          />
          {saving && <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>保存中...</Text>}
        </Card>

        {/* 地区配置 */}
        <Card size="small" title="场景地区配置">
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            配置生成文案时可用的地区选项。AI 会根据选中城市获取当地天气、节气等场景信息。
          </Text>
          <Select
            mode="tags"
            style={{ width: '100%' }}
            placeholder="输入城市名称后按回车添加，如：上海、杭州、义乌"
            value={cities}
            onChange={handleCitiesChange}
            tokenSeparators={[',', '，']}
          >
            {['上海', '北京', '广州', '深圳', '杭州', '成都', '武汉', '南京', '苏州', '义乌'].map(c => (
              <Option key={c} value={c}>{c}</Option>
            ))}
          </Select>
          <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
            可从下拉列表选择或直接输入城市名称。配置后在生成页面可手动切换城市。
          </Text>
        </Card>

        {/* Prompt 模板配置 */}
        <Card size="small" title="Prompt 模板配置">
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            自定义 AI 的系统提示词。留空则使用默认模板，修改后自动保存。
          </Text>

          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text strong>基础系统 Prompt</Text>
              {baseSystemPrompt && (
                <Button type="link" size="small" icon={<UndoOutlined />} onClick={() => handleResetPrompt('BASE')}>
                  恢复默认
                </Button>
              )}
            </div>
            <TextArea
              rows={5}
              placeholder={defaultPrompts.BASE || '默认基础系统提示词...'}
              value={baseSystemPrompt}
              onChange={handleBasePromptChange}
              maxLength={5000}
              showCount
            />
          </div>

          <Divider style={{ margin: '12px 0' }} />

          <Text strong style={{ display: 'block', marginBottom: 8 }}>各类型 Prompt 覆盖</Text>
          <Collapse ghost size="small">
            {CONTENT_TYPES.map(ct => (
              <Panel
                header={
                  <Space>
                    {ct.label}
                    {promptOverrides[ct.key] && <Tag color="blue" style={{ fontSize: 10 }}>已自定义</Tag>}
                  </Space>
                }
                key={ct.key}
                extra={
                  promptOverrides[ct.key] ? (
                    <Button
                      type="link"
                      size="small"
                      icon={<UndoOutlined />}
                      onClick={(e) => { e.stopPropagation(); handleResetPrompt(ct.key); }}
                    >
                      恢复默认
                    </Button>
                  ) : null
                }
              >
                <TextArea
                  rows={4}
                  placeholder={defaultPrompts[ct.key] || `${ct.label}类型的默认提示词...`}
                  value={promptOverrides[ct.key] || ''}
                  onChange={(e) => handlePromptOverrideChange(ct.key, e.target.value)}
                  maxLength={5000}
                  showCount
                />
              </Panel>
            ))}
          </Collapse>
          {saving && <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>保存中...</Text>}
        </Card>
      </Space>

      {/* 供应商配置弹窗 */}
      <Modal
        title="配置AI供应商"
        open={configModalVisible}
        onOk={handleSaveConfig}
        onCancel={() => setConfigModalVisible(false)}
        okText="保存"
        cancelText="取消"
        width={500}
      >
        <Form form={configForm} layout="vertical">
          <Form.Item name="provider" label="供应商" rules={[{ required: true, message: '请选择供应商' }]}>
            <Select placeholder="选择供应商">
              {Object.entries(PROVIDER_NAMES).map(([key, name]) => (
                <Option key={key} value={key}>{name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="apiKey" label="API Key" rules={[{ required: true, message: '请输入API Key' }]}>
            <Input.Password placeholder="输入API Key" />
          </Form.Item>
          <Form.Item name="baseUrl" label="自定义Base URL（可选）">
            <Input placeholder="如使用中转服务，填写自定义URL" />
          </Form.Item>
          <Form.Item name="modelName" label="默认模型（可选）">
            <Input placeholder="如：qwen-plus, deepseek-chat" />
          </Form.Item>
          <Form.Item name="isDefault" label="设为默认供应商" initialValue={false}>
            <Select>
              <Option value={true}>是</Option>
              <Option value={false}>否</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AiContentConfig;
